/**
 * Deterministic mock data for the Awaiting Inventory "Wilma Intelligence"
 * row card. Used as a per-field fallback while the underlying analytics
 * fields on `IVehicle` are still being populated upstream so the cell reads
 * as a realistic, fully-populated card instead of a row of em-dashes.
 *
 * Values are seeded from `vehicle.id` (or VRM as a backup) so a given
 * vehicle always shows the same numbers across renders, but are weighted
 * by make tier, vehicle age and mileage so premium low-mileage cars show
 * stronger analytics than old budget cars — roughly matching what an
 * operator would expect to see from real data.
 *
 * Once the real backend fields are populated, the cell prefers those and
 * only falls back to these on a per-field basis.
 */

import { IVehicle } from "../../models/Vehicle";

/** Tiny deterministic 0..1 PRNG seeded from a string (FNV-1a + mulberry32). */
function seededRand(seed: string) {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return () => {
    h += 0x6d2b79f5;
    let t = h;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const between = (rand: () => number, min: number, max: number) =>
  Math.round(min + rand() * (max - min));

const betweenF = (rand: () => number, min: number, max: number) =>
  min + rand() * (max - min);

const roundTo = (value: number, step: number) =>
  Math.round(value / step) * step;

const clamp = (n: number, min: number, max: number) =>
  Math.max(min, Math.min(max, n));

// Rough UK-market parts-yield tiers. Premium German + luxury SUV marques
// return meaningfully more per donor vehicle than budget hatchbacks; EV
// makers sit a tier higher again thanks to expensive drive units.
const MAKE_TIERS: Record<string, number> = {
  // Tier ~1.6 — luxury / exotic
  porsche: 1.6,
  "aston martin": 1.6,
  bentley: 1.6,
  lamborghini: 1.7,
  ferrari: 1.7,
  maserati: 1.5,
  "rolls-royce": 1.7,
  tesla: 1.5,
  // Tier ~1.3–1.4 — premium German / luxury SUV
  "land rover": 1.4,
  "range rover": 1.4,
  audi: 1.3,
  bmw: 1.3,
  "mercedes-benz": 1.35,
  mercedes: 1.35,
  jaguar: 1.25,
  volvo: 1.15,
  lexus: 1.2,
  // Tier ~1.0 — mainstream
  volkswagen: 1.05,
  ford: 1.0,
  vauxhall: 0.9,
  opel: 0.9,
  peugeot: 0.9,
  renault: 0.9,
  citroen: 0.9,
  seat: 0.95,
  skoda: 1.0,
  toyota: 1.0,
  honda: 1.0,
  mazda: 1.0,
  nissan: 0.95,
  hyundai: 0.9,
  kia: 0.9,
  mini: 1.05,
  // Tier ~0.8 — budget / small
  dacia: 0.7,
  suzuki: 0.8,
  fiat: 0.8,
  smart: 0.75,
  chevrolet: 0.85,
};

const getMakeMultiplier = (make: string | null | undefined): number => {
  if (!make) return 1.0;
  const key = make.toLowerCase().trim();
  if (MAKE_TIERS[key] !== undefined) return MAKE_TIERS[key];
  // Partial match on first token so "BMW M3" still resolves.
  const firstWord = key.split(/\s+/)[0];
  if (MAKE_TIERS[firstWord] !== undefined) return MAKE_TIERS[firstWord];
  return 1.0;
};

export interface IMockVehicleIntelligence {
  aiPartsIndex: number;
  totalValueAllParts: number;
  projectedSales: number;
  valuePartsInStock: number;
  totalPartsInStock: number;
  engineValue: number;
  gearboxValue: number;
  exportValue: number;
  valuationTradePoor: number;
  recommendedPurchasePrice: number;
}

export function buildMockVehicleIntelligence(
  vehicle: IVehicle
): IMockVehicleIntelligence {
  const seed = vehicle.id || vehicle.vrm || "demo-vehicle";
  const rand = seededRand(seed);

  // ---- Vehicle weighting signals ---------------------------------------

  const make = vehicle.combinedMake ?? vehicle.dvlaMake ?? null;
  const makeMultiplier = getMakeMultiplier(make);

  const year = vehicle.dvlaYearOfManufacture ?? null;
  const currentYear = new Date().getFullYear();
  // 0..1 "youth" signal — 2020+ cars tend to 1, early-2000s cars tend to 0.
  let youth = 0.55;
  if (year) {
    const age = currentYear - year;
    youth = clamp(1 - age / 22, 0.15, 1);
  }

  const miles = vehicle.odometerInMiles ?? null;
  // 0..1 "freshness" signal — <40k miles ~ 1, >200k miles ~ 0.25.
  let freshness = 0.6;
  if (miles !== null && miles !== undefined) {
    freshness = clamp(1 - (miles - 40000) / 200000, 0.2, 1);
  }

  // Composite "quality" — drives both the AI score and the scale of the
  // financial figures. Stays within a reasonable band so results don't
  // swing too wildly just from a single missing field.
  const quality = clamp(
    0.35 * makeMultiplier * 0.7 + 0.35 * youth + 0.3 * freshness + 0.1,
    0.4,
    1.25
  );

  // ---- Component values (engine, gearbox, export) ----------------------
  // These are the building blocks of the "total value" figure so the
  // aggregate totals stay consistent with the per-component tiles.

  const engineValue = roundTo(
    betweenF(rand, 350, 1200) * quality * makeMultiplier,
    5
  );
  const gearboxValue = roundTo(
    betweenF(rand, 200, 700) * quality * makeMultiplier,
    5
  );
  const exportValue = roundTo(
    betweenF(rand, 120, 500) * quality,
    5
  );

  // Body, interior, electrical, glass, trim. Scales with the drivetrain
  // value but with its own jitter so two vehicles with the same engine
  // don't end up identical.
  const otherPartsValue = roundTo(
    (engineValue + gearboxValue + exportValue) * betweenF(rand, 1.2, 2.0),
    25
  );

  const totalValueAllParts = roundTo(
    engineValue + gearboxValue + exportValue + otherPartsValue,
    25
  );

  // Realistic sell-through — you rarely achieve 100% of "theoretical"
  // parts value; 55–75% is a reasonable planning assumption once
  // damage/shrinkage/slow-movers are taken into account.
  const projectedSales = roundTo(
    totalValueAllParts * betweenF(rand, 0.55, 0.75),
    25
  );

  // Awaiting-inventory vehicles usually aren't dismantled yet, so "value
  // in stock" is a small fraction (parts pulled for pre-orders / quick
  // wins) and often zero.
  const inStockShare = rand() < 0.35 ? 0 : betweenF(rand, 0.05, 0.2);
  const valuePartsInStock = roundTo(totalValueAllParts * inStockShare, 25);
  const totalPartsInStock =
    inStockShare === 0 ? 0 : between(rand, 1, 14);

  // ---- Buy-side signals -------------------------------------------------

  // Salvage / trade-poor valuation — loosely a fraction of projected sales
  // floor-adjusted for scrap weight. Lands £200–£1,600 for typical cars,
  // higher for premium SUVs.
  const valuationTradePoor = roundTo(
    clamp(projectedSales * betweenF(rand, 0.12, 0.22), 180, 1800),
    25
  );

  // Recommended purchase — the price Wilma suggests is still margin-safe.
  // Typically 22–38% of projected sales and always at least a clear
  // margin above pure salvage.
  const recommendedPurchasePrice = roundTo(
    Math.max(
      valuationTradePoor + 150,
      projectedSales * betweenF(rand, 0.22, 0.38)
    ),
    25
  );

  // ---- Recommendation score --------------------------------------------
  // Composite of the signals above, translated into the 0–100 space the
  // rest of the UI expects. Headline score reflects how attractive the
  // vehicle is as a dismantling candidate.

  const scoreBase =
    45 * (makeMultiplier / 1.2) + 30 * youth + 20 * freshness;
  const aiPartsIndex = clamp(
    Math.round(scoreBase + between(rand, -5, 6)),
    32,
    97
  );

  return {
    aiPartsIndex,
    totalValueAllParts,
    projectedSales,
    valuePartsInStock,
    totalPartsInStock,
    engineValue,
    gearboxValue,
    exportValue,
    valuationTradePoor,
    recommendedPurchasePrice,
  };
}
